import React, { useEffect, useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  Hammer,
  ArrowRight,
  Shield,
  Briefcase,
  Building2,
  Wrench,
  Eye,
  EyeOff,
  Sparkles,
  Zap,
  Users as UsersIcon,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";

const ORG_META = {
  platform: {
    label: "Platforma",
    Icon: Sparkles,
    gradient: "from-[#A855F7] to-[#7E22CE]",
  },
  aayda: {
    label: "AAYDA — Sifarişçi",
    Icon: Building2,
    gradient: "from-[#3B82F6] to-[#1D4ED8]",
  },
  consulting: {
    label: "Konsaltinq",
    Icon: Briefcase,
    gradient: "from-[#10B981] to-[#047857]",
  },
  contractor: {
    label: "Podratçı",
    Icon: Wrench,
    gradient: "from-[#F97316] to-[#C2410C]",
  },
};

const DEFAULT_PASSWORD_FOR = (role) =>
  role === "superadmin" ? "Admin123!" : "Demo123!";

export default function Login() {
  const { login, user } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@yoltikinti.az");
  const [password, setPassword] = useState("Admin123!");
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [openOrg, setOpenOrg] = useState({
    platform: false,
    aayda: false,
    consulting: false,
    contractor: false,
  });

  useEffect(() => {
    api.get("/auth/public-users").then((r) => setUsers(r.data)).catch(() => {});
  }, []);

  if (user) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Sistemə daxil olundu");
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Daxil olmaq mümkün olmadı");
    } finally {
      setLoading(false);
    }
  };

  const quickLogin = async (u) => {
    const pw = DEFAULT_PASSWORD_FOR(u.role);
    setEmail(u.email);
    setPassword(pw);
    setLoading(true);
    try {
      await login(u.email, pw);
      toast.success(`Daxil olundu: ${u.full_name}`);
      nav("/");
    } catch (e) {
      toast.error("Şifrə uyğun deyil. Manual daxil edin.");
    } finally {
      setLoading(false);
    }
  };

  const grouped = users.reduce((acc, u) => {
    const k = u.organization_type || "platform";
    if (!acc[k]) acc[k] = [];
    acc[k].push(u);
    return acc;
  }, {});

  const orgOrder = ["platform", "aayda", "consulting", "contractor"];

  return (
    <div className="min-h-screen bg-aurora relative overflow-hidden text-white">
      {/* Decorative glow blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 w-[480px] h-[480px] rounded-full bg-[#A855F7] opacity-25 blur-3xl" />
      <div className="pointer-events-none absolute top-1/3 right-0 w-[420px] h-[420px] rounded-full bg-[#FF6B35] opacity-20 blur-3xl" />

      {/* Top bar */}
      <div className="absolute top-6 right-6 z-20">
        <button
          onClick={() => setLang(lang === "az" ? "en" : "az")}
          className="text-xs font-bold mono px-3 py-2 rounded-xl bg-white/10 backdrop-blur-md border border-white/15 hover:bg-white/20 transition-colors"
          data-testid="lang-toggle-login"
        >
          {lang === "az" ? "EN" : "AZ"}
        </button>
      </div>

      <div className="relative z-10 min-h-screen grid lg:grid-cols-2 gap-8 px-6 sm:px-10 py-8">
        {/* LEFT — Hero */}
        <div className="flex flex-col justify-between max-w-xl">
          <div>
            <div className="flex items-center gap-3 mb-12">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#FF6B35] to-[#E85D2A] flex items-center justify-center glow-accent shrink-0">
                <Hammer className="w-6 h-6 text-white" />
              </div>
              <div>
                <div className="font-heading font-black text-lg leading-tight tracking-tight">
                  Infrastructure
                </div>
                <div className="font-heading font-black text-lg leading-tight tracking-tight text-white/70">
                  Supervision & Advisory
                </div>
              </div>
            </div>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-dark mb-8">
              <Sparkles className="w-3.5 h-3.5 text-[#FF6B35]" />
              <span className="text-xs font-semibold">Ən müasir nəzarət sistemi</span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl font-black tracking-tighter leading-[0.95]">
              Tikinti
              <br />
              proseslərinə
              <br />
              <span className="text-[#FF6B35]">tam nəzarət.</span>
            </h1>

            <p className="text-slate-300 mt-8 max-w-md text-sm sm:text-base leading-relaxed">
              Yol və infrastruktur tikintisi proseslərinin idarəetmə və nəzarət sistemi.
              <span className="text-white">AAYDA · Konsaltinq · Podratçı</span> arasında
              ardıcıl yoxlanış zənciri, real-vaxt hesablama və media arxivi.
            </p>
          </div>

          {/* Feature cards */}
          <div className="mt-12 space-y-3 max-w-md">
            {[
              { Icon: Zap, title: "Real-vaxt izləmə", body: "Canlı yeniləmələr və bildirişlər" },
              { Icon: Shield, title: "Təhlükəsiz sistem", body: "Bank səviyyəsində şifrələmə" },
              { Icon: UsersIcon, title: "Komanda əməkdaşlığı", body: "Bütün tərəfdaşlar bir yerdə" },
            ].map((f) => (
              <div
                key={f.title}
                className="flex items-center gap-4 glass-dark rounded-2xl p-4"
              >
                <div className="w-11 h-11 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center shrink-0">
                  <f.Icon className="w-5 h-5 text-[#FF6B35]" />
                </div>
                <div>
                  <div className="text-sm font-bold text-white">{f.title}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{f.body}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Stats footer */}
          <div className="hidden sm:flex gap-10 pt-10 mt-8 border-t border-white/10">
            <div>
              <div className="font-heading font-black text-3xl">
                {users.length}+
              </div>
              <div className="text-xs text-slate-400 mt-1">İstifadəçi</div>
            </div>
            <div>
              <div className="font-heading font-black text-3xl">4</div>
              <div className="text-xs text-slate-400 mt-1">Təşkilat</div>
            </div>
            <div>
              <div className="font-heading font-black text-3xl">99.9%</div>
              <div className="text-xs text-slate-400 mt-1">Uptime</div>
            </div>
          </div>
        </div>

        {/* RIGHT — Login card */}
        <div className="flex items-center justify-center lg:justify-end">
          <div className="w-full max-w-md bg-white rounded-[28px] p-8 sm:p-10 shadow-2xl text-slate-900">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-50 text-[#2563EB] text-xs font-bold mb-6">
              <CheckCircle2 className="w-3.5 h-3.5" />
              XOŞ GƏLDINIZ
            </div>
            <h2 className="font-heading text-4xl font-black tracking-tight mb-2">
              Sistemə Daxil Ol
            </h2>
            <p className="text-sm text-slate-500 mb-7">
              Hesabınıza daxil olun və işə başlayın
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="label-overline text-slate-600">
                  Email Ünvan
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="bg-slate-50 border-slate-200 rounded-xl py-6 text-base mt-1.5"
                  data-testid="login-email"
                />
              </div>
              <div>
                <Label htmlFor="password" className="label-overline text-slate-600">
                  Şifrə
                </Label>
                <div className="relative mt-1.5">
                  <Input
                    id="password"
                    type={showPass ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="bg-slate-50 border-slate-200 rounded-xl py-6 text-base pr-12"
                    data-testid="login-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
                    data-testid="toggle-password"
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-[#2563EB] to-[#1D4ED8] hover:from-[#1D4ED8] hover:to-[#1E40AF] text-white rounded-xl py-6 text-base font-bold glow-brand"
                data-testid="login-submit"
              >
                {loading ? "..." : "Sistemə Daxil Ol"}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </form>

            {/* Quick access org groups (dropdown style) */}
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="text-center mb-4">
                <div className="label-overline text-slate-500">Sürətli Giriş</div>
                <div className="text-[11px] text-slate-400 mt-1">
                  {users.length} istifadəçi · təşkilata görə qruplaşdırılıb
                </div>
              </div>
              <div className="space-y-2" data-testid="user-picker">
                {orgOrder.map((orgKey) => {
                  const list = grouped[orgKey];
                  if (!list || list.length === 0) return null;
                  const meta = ORG_META[orgKey];
                  const isOpen = openOrg[orgKey];
                  return (
                    <div
                      key={orgKey}
                      className="rounded-2xl border border-slate-200 overflow-hidden bg-white"
                      data-testid={`org-group-${orgKey}`}
                    >
                      <button
                        type="button"
                        onClick={() =>
                          setOpenOrg((s) => ({ ...s, [orgKey]: !s[orgKey] }))
                        }
                        className="w-full flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
                        data-testid={`org-toggle-${orgKey}`}
                      >
                        <div
                          className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center shadow-md shrink-0`}
                        >
                          <meta.Icon className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 text-left min-w-0">
                          <div className="text-sm font-bold text-slate-900 truncate">
                            {meta.label}
                          </div>
                          <div className="text-[11px] text-slate-500 mono">
                            {list.length} nəfər
                          </div>
                        </div>
                        {isOpen ? (
                          <ChevronUp className="w-4 h-4 text-slate-400 shrink-0" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-slate-400 shrink-0" />
                        )}
                      </button>
                      {isOpen && (
                        <div className="border-t border-slate-100 divide-y divide-slate-100 bg-slate-50/50 max-h-64 overflow-y-auto scroll-thin">
                          {list.map((u) => (
                            <button
                              key={u.id}
                              type="button"
                              onClick={() => quickLogin(u)}
                              disabled={loading}
                              className="w-full flex items-center gap-3 p-2.5 hover:bg-white text-left transition-colors disabled:opacity-50"
                              data-testid={`user-${u.role}-${u.id}`}
                            >
                              <div
                                className={`w-8 h-8 rounded-lg bg-gradient-to-br ${meta.gradient} flex items-center justify-center text-white text-xs font-bold shrink-0`}
                              >
                                {u.full_name?.[0]?.toUpperCase() || "?"}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-slate-900 truncate">
                                  {u.full_name}
                                </div>
                                <div className="text-[10px] text-slate-500 mono truncate">
                                  {t(`role_label.${u.role}`)} · {u.email}
                                </div>
                              </div>
                              <ArrowRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 text-[10px] text-slate-400 mono text-center">
                Default şifrələr · superadmin: <strong>Admin123!</strong> · digər:{" "}
                <strong>Demo123!</strong>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
